import { User } from './types/user';

export type Theme = 'light' | 'dark';

export interface ThemeColors {
  primary: string;
  secondary: string;
  background: string;
  surface: string;
  text: string;
  textSecondary: string;
  border: string;
  hover: string;
}

export const themes: Record<Theme, ThemeColors> = {
  light: {
    primary: '#469AD4',
    secondary: '#FBA927',
    background: '#f8fafc',
    surface: '#ffffff',
    text: '#1e293b',
    textSecondary: '#64748b',
    border: '#e2e8f0',
    hover: '#f1f5f9'
  },
  dark: {
    primary: '#469AD4',
    secondary: '#FBA927',
    background: '#0f172a',
    surface: '#1e293b',
    text: '#f8fafc',
    textSecondary: '#94a3b8',
    border: '#334155',
    hover: '#334155'
  }
};

export interface Topic {
  id: string;
  title: string;
  description: string;
  category: Category;
  author: User;
  createdAt: Date;
  type: 'article' | 'video' | 'post';
  content: string;
  thumbnailUrl?: string;
  tags?: string[];
  likes?: number;
  likedBy?: string[];
  views?: number;
}

export interface Category {
  id: string;
  name: string;
  slug: string;
  description: string;
  icon: string;
  subcategories?: Category[];
}

export const CATEGORIES: Category[] = [
  {
    id: 'tech',
    name: 'Technology',
    slug: 'technology',
    description: 'Latest in technology and innovation',
    icon: 'Cpu',
    subcategories: [
      {
        id: 'hardware',
        name: 'Hardware',
        slug: 'hardware',
        description: 'Computer hardware and infrastructure',
        icon: 'HardDrive'
      },
      {
        id: 'software',
        name: 'Software',
        slug: 'software',
        description: 'Software development and applications',
        icon: 'Code2'
      },
      {
        id: 'cybersecurity',
        name: 'Cybersecurity',
        slug: 'cybersecurity',
        description: 'Security and data protection',
        icon: 'Shield'
      }
    ]
  },
  {
    id: 'ai-ml',
    name: 'AI & Machine Learning',
    slug: 'ai-ml',
    description: 'Artificial Intelligence and Machine Learning',
    icon: 'Brain',
    subcategories: [
      {
        id: 'deep-learning',
        name: 'Deep Learning',
        slug: 'deep-learning',
        description: 'Neural networks and deep learning',
        icon: 'Network'
      },
      {
        id: 'nlp',
        name: 'Natural Language Processing',
        slug: 'nlp',
        description: 'Text and language processing',
        icon: 'MessageSquare'
      }
    ]
  },
  {
    id: 'programming',
    name: 'Programming',
    slug: 'programming',
    description: 'Programming languages and development',
    icon: 'Code',
    subcategories: [
      {
        id: 'web-dev',
        name: 'Web Development',
        slug: 'web-development',
        description: 'Web technologies and frameworks',
        icon: 'Globe'
      },
      {
        id: 'mobile-dev',
        name: 'Mobile Development',
        slug: 'mobile-development',
        description: 'Mobile app development',
        icon: 'Smartphone'
      }
    ]
  },
  {
    id: 'blockchain',
    name: 'Blockchain',
    slug: 'blockchain',
    description: 'Blockchain and cryptocurrency',
    icon: 'Link',
    subcategories: [
      {
        id: 'crypto',
        name: 'Cryptocurrency',
        slug: 'cryptocurrency',
        description: 'Digital currencies and tokens',
        icon: 'Coins'
      },
      {
        id: 'smart-contracts',
        name: 'Smart Contracts',
        slug: 'smart-contracts',
        description: 'Blockchain applications and smart contracts',
        icon: 'FileContract'
      }
    ]
  },
  {
    id: 'engineering',
    name: 'Engineering',
    slug: 'engineering',
    description: 'Engineering disciplines and innovations',
    icon: 'Cog',
    subcategories: [
      {
        id: 'software-eng',
        name: 'Software Engineering',
        slug: 'software-engineering',
        description: 'Software architecture and design',
        icon: 'GitBranch'
      },
      {
        id: 'systems-eng',
        name: 'Systems Engineering',
        slug: 'systems-engineering',
        description: 'Complex systems and integration',
        icon: 'Settings2'
      }
    ]
  },
  {
    id: 'research',
    name: 'Research & Development',
    slug: 'research',
    description: 'Technical research and innovation',
    icon: 'FlaskConical',
    subcategories: [
      {
        id: 'academic',
        name: 'Academic Research',
        slug: 'academic-research',
        description: 'Academic papers and studies',
        icon: 'GraduationCap'
      },
      {
        id: 'industry',
        name: 'Industry Research',
        slug: 'industry-research',
        description: 'Industry reports and analysis',
        icon: 'Building2'
      }
    ]
  },
  {
    id: 'business',
    name: 'Business & Strategy',
    slug: 'business',
    description: 'Business insights and strategy',
    icon: 'Briefcase',
    subcategories: [
      {
        id: 'management',
        name: 'Management',
        slug: 'management',
        description: 'Business management and leadership',
        icon: 'Users'
      },
      {
        id: 'analytics',
        name: 'Business Analytics',
        slug: 'analytics',
        description: 'Data analysis and business intelligence',
        icon: 'BarChart'
      }
    ]
  }
];